import {UHttp} from "../../utils/UHttp"
import {OptionCategory} from './option-category-model';
import {collaboratorsByCompany} from './compagny-model';

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

// Types pour les services
export interface Service {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration?: number; // en minutes
  companyId: string; // ID de l'entreprise
  image?: string;
  imageVariants?: {
    original?: string;
    medium?: string;
  };
  isActive: boolean;
  requiresBooking: boolean;
  minTimeBeforeBooking?: number;
  bufferTimeBetweenSlots?: number;
  bookings?: number;
  createdAt?: string;
  updatedAt?: string;
  optionCategories?: OptionCategory[];
  collaborators?: collaboratorsByCompany[];
  collaboratorIds?: string[];
  company?: any;

  // Configuration des acomptes
  depositRequired?: boolean;
  depositType?: 'percentage' | 'fixed' | null;
  depositValue?: number | null;
  cancellationRefundDeadlineHours?: number | null;
}

// Types pour les réponses API
export interface ServiceResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ServicesResponse {
  success: boolean;
  message: string;
  data: Service[];
}

export interface CreateServiceData {
  name: string;
  description?: string;
  price: number;
  duration?: number;
  companyId?: string;
  requiresBooking: boolean;
  isActive?: boolean;
  minTimeBeforeBooking?: number;
  bufferTimeBetweenSlots?: number;
}

export interface UpdateServiceData extends Partial<CreateServiceData> {}

export class ServiceModel {
  private static instance: ServiceModel;

  private constructor() {}

  public static getInstance(): ServiceModel {
    if (!ServiceModel.instance) {
      ServiceModel.instance = new ServiceModel();
    }
    return ServiceModel.instance;
  }

  public async getServices(companyId: string): Promise<ServicesResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/service/company?companyId=${companyId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error ServiceModel.getServices:', error);
      throw error;
    }
  }

  public async getServiceById(serviceId: string): Promise<ServiceResponse<Service>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/service/detail/${serviceId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error ServiceModel.getServiceById ${serviceId}:`, error);
      throw error;
    }
  }

  public async createService(serviceData: FormData): Promise<ServiceResponse<Service>> {
    try {
      const response = await UHttp.postFormData(
        `${apiUrlServer}/api/service/company`,
        serviceData,
        UHttp.authHeaders()
      );
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error('Error ServiceModel.createService:', error);
      throw error;
    }
  }

  public async updateService(serviceId: string, serviceData: FormData): Promise<ServiceResponse<Service>> {
    try {
      const response = await UHttp.putFormData(
        `${apiUrlServer}/api/service/update/${serviceId}`,
        serviceData,
        UHttp.authHeaders()
      );
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error ServiceModel.updateService ${serviceId}:`, error);
      throw error;
    }
  }

  public async toggleServiceStatus(serviceId: string, isActive: boolean): Promise<ServiceResponse<Service>> {
    try {
      let response;
      if (isActive) {
        response = await UHttp.put(
          `${apiUrlServer}/api/service/enable/${serviceId}`,
          {},
          UHttp.authHeaders()
        );
      } else {
        response = await UHttp.delete(
          `${apiUrlServer}/api/service/disable/${serviceId}`,
          UHttp.authHeaders()
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error ServiceModel.toggleServiceStatus ${serviceId}:`, error);
      throw error;
    }
  }

  public async deleteService(serviceId: string, deleteRelated: boolean = true): Promise<ServiceResponse> {
    try {
      const response = await UHttp.delete(
        `${apiUrlServer}/api/service/delete/${serviceId}?deleteRelated=${deleteRelated}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error ServiceModel.deleteService ${serviceId}:`, error);
      throw error;
    }
  }

  /**
   * Récupère les collaborateurs assignés à un service
   * @param serviceId ID du service
   * @returns Liste des collaborateurs assignés
   */
  public async getServiceCollaborators(serviceId: string): Promise<any> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/service/collaborators/${serviceId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error ServiceModel.getServiceCollaborators ${serviceId}:`, error);
      throw error;
    }
  }

  /**
   * Ajoute un collaborateur à un service
   * @param serviceId ID du service
   * @param collaboratorId ID du collaborateur à ajouter
   * @returns Réponse de l'API
   */
  public async addCollaboratorToService(serviceId: string, collaboratorId: string): Promise<any> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/service/collaborators/add/${serviceId}`,
        { collaboratorCompagnieId: collaboratorId },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error ServiceModel.addCollaboratorToService ${serviceId}:`, error);
      throw error;
    }
  }

  /**
   * Retire un collaborateur d'un service
   * @param serviceId ID du service
   * @param collaboratorId ID du collaborateur à retirer
   * @returns Réponse de l'API
   */
  public async removeCollaboratorFromService(serviceId: string, collaboratorId: string): Promise<any> {
    try {
      const response = await UHttp.delete(
        `${apiUrlServer}/api/service/collaborators/remove/${serviceId}/${collaboratorId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error ServiceModel.removeCollaboratorFromService ${serviceId}:`, error);
      throw error;
    }
  }

  /**
   * Synchronise les collaborateurs d'un service en une seule requête (batch operation)
   * @param serviceId ID du service
   * @param collaboratorIds Liste des IDs des collaborateurs à assigner
   * @returns Réponse de l'API
   */
  public async syncCollaborators(serviceId: string, collaboratorIds: string[]): Promise<any> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/service/collaborators/sync/${serviceId}`,
        { collaboratorIds },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error ServiceModel.syncCollaborators ${serviceId}:`, error);
      throw error;
    }
  }
}